import React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { OnBoardingTask } from '../../../store/user/types';
import { GettingStartedUtils } from '../../../utils';
import { TasksList } from './TasksList';
import { TaskDetails } from './TaskDetails';
import { HomePageSection } from '../HomePageSection';
import { RegularCardBox } from '../../Cards/RegularCardBox';
import { FlagsContext } from '../../../context';
import { usePatchDismissalStateMutation } from '../../../services/api/improvedOnboardingApi';
import { AlertSnackbar } from '../../AlertSnackbar/AlertSnackbar';
import { AlertVariant } from '../../../store/ui/types';

interface SetupGuideSectionProps {
  tasks: OnBoardingTask[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    flexContainer: {
      display: 'flex',
      minHeight: 230,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    improvedOnboardingCard: {
      height: '100px',
    },
    sectionHeaderWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0.5, 0),
    },
  }),
);

export const SetupGuideSection: React.FC<SetupGuideSectionProps> = ({
  tasks,
}) => {
  const classes = useStyles();
  const [patchDismissalState] = usePatchDismissalStateMutation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isDismissed, setIsDismissed] = React.useState(false);

  const { EnableImprovedOnboarding } = React.useContext(FlagsContext);

  const initialSelectedTask = tasks.find(t => !t.fields.done);
  const [activeTaskCode, setActiveTaskCode] = React.useState<
    string | undefined
  >(initialSelectedTask?.fields.taskCode);
  const todoTasksCount = tasks.filter(task => !task.fields.done).length;

  const activeTaskDetails = React.useMemo(
    () =>
      activeTaskCode
        ? {
            ...GettingStartedUtils.MapTaskCodeToTaskDetails(
              activeTaskCode,
              true,
            ),
            path: GettingStartedUtils.MapTaskCodeToRoutePath(
              activeTaskCode,
              true,
            ),
            illustrationName: GettingStartedUtils.MapTaskCodeToIllustrationName(
              activeTaskCode,
            ),
          }
        : null,
    [activeTaskCode],
  );

  React.useEffect(() => {
    setActiveTaskCode(initialSelectedTask?.fields.taskCode);
  }, [initialSelectedTask]);

  const handleSelectTask = (code: string) => {
    setActiveTaskCode(code);
  };

  // return null when all tasks are done in order to show the empty state
  if (!todoTasksCount || !activeTaskDetails || !activeTaskCode) {
    return null;
  }

  const handleUndoDismiss = async () => {
    await patchDismissalState({
      fields: { dismissed: false },
    });
    setIsDismissed(false);
  };

  const successDismissSnackbar = React.useCallback(
    (snackbarKey: string) => (
      <div key={snackbarKey}>
        <AlertSnackbar
          variant={AlertVariant.SUCCESS}
          onClose={() => closeSnackbar(snackbarKey)}
          alertMessage="Card dismissed"
          onAction={handleUndoDismiss}
          actionLabel="Undo"
        />
      </div>
    ),
    [closeSnackbar],
  );

  const handleDismissImprovedOnboarding = async () => {
    const onboardingStatus = await patchDismissalState({
      fields: { dismissed: true },
    }).unwrap();

    if (onboardingStatus.fields.dismissed) {
      setIsDismissed(true);
      // show snackbar to allow re-enabling
      enqueueSnackbar('Card has been dismissed', {
        content: (snackbarKey: string) => successDismissSnackbar(snackbarKey),
      });
    }
  };

  const currentCard = (
    <RegularCardBox>
      <div className={classes.flexContainer}>
        <TasksList
          tasks={tasks}
          activeTaskCode={activeTaskCode}
          onSelect={handleSelectTask}
        />
        <TaskDetails taskData={activeTaskDetails} />
      </div>
    </RegularCardBox>
  );

  return (
    <HomePageSection
      title="Setup guide"
      subTitle={`${todoTasksCount} step${todoTasksCount > 1 ? 's' : ''} left`}
    >
      {EnableImprovedOnboarding && !isDismissed ? (
        <RegularCardBox onDismissClick={handleDismissImprovedOnboarding}>
          <div className={classes.improvedOnboardingCard}>
            Improved Onboarding
          </div>
        </RegularCardBox>
      ) : (
        currentCard
      )}
    </HomePageSection>
  );
};
